import {autoRegister, resolve} from "../../container";
import {GLOBAL, type GlobalWindow} from "../../common/globals";
import {Load} from "../../common/load";
import {ColorSchemeService} from "../../common/colorSchemeService";
import {LanguagesService} from "../../common/languages";
import {Configuration, Properties} from "../../common/config";
import {elementFrom} from "../../common/utils/html";
import {isDefined} from "../../common/utils/basics";
import type {Maybe} from "../../bootstrap/common/types";
import {omitUndefined} from "../../common/utils/objects";

export type GoogleTagManagerWindow = GlobalWindow & {
    dataLayer: any;
};

const WEBSITE_CORPORATE_ZONE = "uk";

@autoRegister()
export class NativeGoogleTagManager {
    private window = GLOBAL.window() as GoogleTagManagerWindow;
    private head = GLOBAL.headElement();
    private body = GLOBAL.bodyElement();

    private GOOGLE_TAG_MANAGER_ID: Maybe<string>;
    private GOOGLE_ANALYTICS_STAGE_FLAG: Maybe<string>;

    public constructor(
        private load: Load = resolve(Load),
        private colorSchemeService: ColorSchemeService = resolve(ColorSchemeService),
        private languagesService: LanguagesService = resolve(LanguagesService),
        private properties: Properties = resolve(Properties),
        configuration: Configuration = resolve(Configuration)
    ) {
        this.GOOGLE_TAG_MANAGER_ID = configuration.get("GOOGLE_TAG_MANAGER_ID");
        this.GOOGLE_ANALYTICS_STAGE_FLAG = configuration.get("GOOGLE_ANALYTICS_STAGE_FLAG");
    }

    public init(): void {
        this.pushInitialPageObject();
        this.initScript();
    }

    public push(data: any): void {
        this.lazyDataLayer().push(data);
    }

    private pushInitialPageObject(): void {
        this.lazyDataLayer().push({
            page: omitUndefined({
                zone: WEBSITE_CORPORATE_ZONE,
                environment: this.GOOGLE_ANALYTICS_STAGE_FLAG,
                color_mode: this.colorSchemeService.getColorScheme().toLowerCase(),
                page_language: this.languagesService.activeLanguageId(),
                page_area: this.properties.get("pagetype")
            })
        });
    }

    private initScript(): void {
        this.lazyDataLayer().push({
            "event": "gtm.js",
            "gtm.start": Date.now()
        });
        const anchorScript = elementFrom(`<script id="anchor-for-google-tag-manager"></script>`);
        this.head.appendChild(anchorScript);

        if (isDefined(this.GOOGLE_TAG_MANAGER_ID)) {
            void this.load.script(`/metrics/?id=${this.GOOGLE_TAG_MANAGER_ID}`, {}, this.body);
        }
    }

    private lazyDataLayer(): any {
        this.window.dataLayer ??= [];
        return this.window.dataLayer;
    }
}